import pluralize from 'pluralize';
import React, { useEffect, useRef, useState } from 'react';
import SelectAllStudies from '../SelectAllStudies';
import { SimplifiedStudy } from '../types';
import styles from './StudyListToolbar.module.scss';
import { TagPopover } from './components/TagPopover';
import {
  FilterSortDisplayForm,
  SortAndFilterArgs,
  useFilterSortDisplayForm,
} from './components/useFilterSortDisplayForm';
import { StartDiscussionsButton } from './components/StartDiscussionsButton';
import UndoVote from './components/UndoVote';
import { MergeStudiesButton } from './components/MergeStudiesButton';
import { SidebarPanel, SidebarName } from './components/SidebarPanel';
import {
  TAGS_SIDEBAR_ID,
  dispatchSidebarChangeEvent,
  useOpenSidebarRequested,
} from './sidebarEvents';
import { useCriteriaCache } from 'hooks/reviews/useCriteriaCache';
import { useCriteria } from 'hooks/reviews/useCriteria';
import {
  Badge,
  Button,
  Icon,
  IconList,
  Text,
  Toast,
  createToast,
} from 'components/core';
import { EditableCriteriaHandle } from 'components/shared/EditableCriteria';
import { Inline } from 'components/layout';
import { getReviewId } from 'query/review';
import { addTagsToStudies, createTag } from 'query/review/Tags';
import { HighlightsResult } from 'query/review/Highlights';
import { useOnHighlightsSaved } from 'hooks/reviews/useHighlights';
import { useTags } from 'hooks/reviews/useTags';
import { useScrollPadding } from 'hooks/useScrollPadding';
import { criteriaWindowName } from 'pages/CriteriaWindow/windowName';
import { QueryProvider } from 'query/QueryProvider';
import {
  ReviewFeaturesProvider,
  useReviewFeaturesContext,
} from 'contexts/ReviewFeaturesProvider';
import { useUniqueID } from 'hooks/useAccessibleName';
import { useStudyListCookieToasts } from 'pages/StudyList/useStudyListCookieToasts';

export type ToolbarOptions = {
  isHighlightsSidebarEnabled: boolean;
  isDiscussionsEnabled: boolean;
  isMergeStudiesEnabled: boolean;
  isCriteriaEnabled: boolean;
  reviewStage: 'screening' | 'fullText' | 'extraction';
};

type Props = {
  // Toolbar props
  toolbarOptions: ToolbarOptions;
  // Select all props
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // 'Merge as study' props
  onMergeSuccess?: () => void;
  // Discussions props
  selectedStudyIds?: number[];
  studies?: SimplifiedStudy[];
  // Sort & Filter sidebar props
  allowAISort?: boolean;
  sortAndFilter: (args: SortAndFilterArgs) => Promise<any>;
  // Criteria props
  hasCriteria: boolean;
  // Highlights sidebar props
  defaultShowAbstracts: boolean;
  hasHighlights: boolean;
  toggleAbstracts: () => void;
  toggleHighlights: () => void;
  enableHighlights: () => void;
  onHighlightsChange: (highlights: HighlightsResult) => void;
  reviewId?: string;
  // Undo votes
  handleUndo?: () => Promise<any>;
  handleSkip?: () => Promise<any>;
  previouslyVotedStudyTitle?: any;
  onApplyTag: (studyIds: number[], tags: string[]) => void;
  isAwaitingOtherReviewer: boolean;
  areAllStudiesSelected?: boolean;
};

export const StudyListToolbar = (props: Props) => {
  return (
    <QueryProvider>
      <ReviewFeaturesProvider>
        <BaseStudyListToolbar {...props} />
      </ReviewFeaturesProvider>
    </QueryProvider>
  );
};

export const BaseStudyListToolbar = ({
  toolbarOptions,
  handleSelectAll,
  onMergeSuccess,
  selectedStudyIds = [],
  studies = [],
  allowAISort = false,
  sortAndFilter,
  hasCriteria: hasCriteriaAtLoad,
  defaultShowAbstracts,
  hasHighlights,
  toggleAbstracts,
  toggleHighlights,
  enableHighlights,
  onHighlightsChange,
  reviewId,
  handleUndo,
  handleSkip,
  previouslyVotedStudyTitle,
  onApplyTag,
  isAwaitingOtherReviewer,
  areAllStudiesSelected,
}: Props) => {
  useOnHighlightsSaved(onHighlightsChange);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const studiesSelectedCount = selectedStudyIds.length;
  const [allStudiesSelected, setAllStudiesSelected] = useState(false);
  const isMergeStudiesButtonDisabled = studiesSelectedCount < 2;

  const criteriaDisclosureId = useUniqueID({ postfix: 'criteria-disclosure' });
  const criteriaToggleRef = useRef<HTMLButtonElement>(null);
  const highlightsDisclosureId = useUniqueID({
    postfix: 'highlights-disclosure',
  });
  const highlightsToggleRef = useRef<HTMLButtonElement>(null);
  const sortAndFilterDisclosureId = useUniqueID({
    postfix: 'sort-and-filter-disclosure',
  });
  const sortAndFilterToggleRef = useRef<HTMLButtonElement>(null);
  const tagsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const editableCriteriaRef = useRef<EditableCriteriaHandle>(null);
  const criteriaCache = useCriteriaCache();

  const { hasFeature } = useReviewFeaturesContext();
  const isTagsSidebarEnabled = hasFeature('tags_sidebar');

  // Tracks criteria added or cleared since page load; the sidebar fetches them.
  const fetched = useCriteria({ enabled: false });
  const hasCriteria = fetched.criteria
    ? fetched.hasCriteria
    : hasCriteriaAtLoad;

  const [currentSidebar, setCurrentSidebar] = useState<SidebarName | null>(
    null
  );
  const nextSidebar = useRef<SidebarName | null>(null);

  useOpenSidebarRequested(({ name, trigger }) => {
    if (name === 'tags') {
      tagsTriggerRef.current = trigger ?? null;
    }
    if (currentSidebar === name) return;

    openSidebar(name);
  });

  useEffect(() => {
    dispatchSidebarChangeEvent(currentSidebar);
  }, [currentSidebar]);

  const [isShowingAbstracts, setIsShowingAbstracts] = useState(
    defaultShowAbstracts
  );
  const handleToggleAbstracts = () => {
    toggleAbstracts();
    setIsShowingAbstracts((isShowing) => !isShowing);
  };

  const isCriteriaOpen = currentSidebar === 'criteria';
  const isHighlightsOpen = currentSidebar === 'highlights';
  const isSortAndFilterOpen = currentSidebar === 'sortAndFilter';
  const isTagsOpen = currentSidebar === 'tags';

  const sidebarTriggerProps = (name: SidebarName, disclosureId: string) => ({
    role: 'button',
    'aria-expanded': currentSidebar === name,
    'aria-controls': disclosureId,
  });

  // Located here so it renders when the list is and isn't populated
  useStudyListCookieToasts();

  useScrollPadding(toolbarRef);

  const { tags, status: tagsStatus, addTag, refetchTags } = useTags();

  useEffect(() => {
    if (
      // We don't want to set the state if isAllSelected is undefined
      areAllStudiesSelected != undefined &&
      areAllStudiesSelected != allStudiesSelected
    ) {
      setAllStudiesSelected(areAllStudiesSelected);
    }
  }, [areAllStudiesSelected]);

  function handleStudySelection(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setAllStudiesSelected(event.target.checked);
    handleSelectAll(event);
  }

  const {
    form: filterSortDisplayForm,
    sortOptions,
    isResetting,
    numStudiesFound,
    appliedFilterCount,
    applySortAndFilter,
    resetFiltersAndRefresh,
  } = useFilterSortDisplayForm({
    allowAISort,
    sortAndFilter,
    setAllStudiesSelected,
  });
  // Close only after the refresh resolves, so a failed Apply keeps the panel and its choices
  const applySortAndFilterThenClose = async (values: FilterSortDisplayForm) => {
    await applySortAndFilter(values);
    closeSidebar('sortAndFilter');
  };
  const sortAndFilterLabel =
    appliedFilterCount > 0
      ? `Sort & Filter, ${pluralize('filter', appliedFilterCount, true)} active`
      : undefined;

  const applyTags = async (tags: string[]) => {
    try {
      await addTagsToStudies(
        getReviewId() as string,
        selectedStudyIds.map((id) => id.toString()),
        tags
      );

      // Close before onApplyTag clears the selection, so the sidebar exits showing its count
      closeSidebar('tags');
      onApplyTag(selectedStudyIds, tags);
    } catch (error) {
      createToast(
        <Toast type="danger">
          Oops! Something went wrong when applying tags
        </Toast>
      );
      // Rethrow so the tags panel stays open with the user's choices to retry
      throw error;
    }
  };

  const addTags = async (reviewId: string, tagName: string) => {
    try {
      const response = await createTag({ reviewId, tagName });

      await addTag({ id: response.id, name: response.name });
      return response;
    } catch (error) {
      if (error instanceof Error) {
        createToast(
          <Toast type="danger" role="alert">
            Error creating tag: {error.message}
          </Toast>
        );
      }
      // The name may already exist (a colleague created it), so show it to tick instead
      void refetchTags();
      // Rethrow so the panel doesn't select a tag that was never created
      throw error;
    }
  };

  const requestCriteriaClose = () =>
    editableCriteriaRef.current?.requestClose();

  const closeCriteria = () => {
    nextSidebar.current = null;
    requestCriteriaClose();
  };

  const confirmCriteriaClose = () => {
    setCurrentSidebar(nextSidebar.current);
    nextSidebar.current = null;
  };

  const openSidebar = (name: SidebarName) => {
    if (isCriteriaOpen) {
      // Open new sidebar only once the criteria panel agrees to close.
      nextSidebar.current = name;
      requestCriteriaClose();
      return;
    }

    setCurrentSidebar(name);
  };

  const closeSidebar = (name: Exclude<SidebarName, 'criteria'>) => {
    setCurrentSidebar((current) => (current === name ? null : current));
  };

  const toggleCriteriaPanel = () => {
    if (isCriteriaOpen) {
      closeCriteria();
    } else {
      // Force re-fetch on each open
      criteriaCache.invalidate();
      openSidebar('criteria');
    }
  };

  const criteriaTrigger = hasCriteria
    ? ({
        label: 'Eligibility criteria',
        icon: IconList.light.faListCheck,
        type: isCriteriaOpen ? 'primary' : 'neutral',
        variant: 'outline',
        pendoKey: 'show-criteria',
      } as const)
    : ({
        label: 'Add eligibility criteria',
        icon: IconList.light.faPlus,
        type: 'primary',
        variant: 'solid',
        pendoKey: 'add-criteria',
      } as const);

  const criteriaWindowRef = useRef<Window | null>(null);

  const openCriteriaWindow = () => {
    const id = reviewId || getReviewId();
    if (!id) return;

    // Navigating the open popup would discard an edit in progress, so focus it.
    const openWindow = criteriaWindowRef.current;
    if (openWindow && !openWindow.closed) {
      openWindow.focus();
      closeCriteria();
      return;
    }

    // A per-review window name reuses that review's existing popup instead of
    // spawning duplicates (the fallback when this tab reloaded and lost the
    // ref); opening on click (not on load) keeps this clear of popup blockers.
    const criteriaWindow = window.open(
      `/reviews/${id}/criteria_window`,
      criteriaWindowName(id),
      'popup,width=345,height=600'
    );

    // Popup blocked - keep the criteria panel so the reviewer isn't left with
    // nothing, and say why the window didn't appear.
    if (!criteriaWindow) {
      createToast(
        <Toast type="danger" role="alert">
          Your browser blocked the criteria window. Allow pop-ups for Covidence
          and try again.
        </Toast>
      );
      return;
    }

    criteriaWindowRef.current = criteriaWindow;
    criteriaWindow.focus();
    closeCriteria();
  };

  const studyCountText = `(${studiesSelectedCount} ${pluralize(
    'study',
    studiesSelectedCount
  )})`;

  const tagsPanelProps = {
    reviewId: reviewId || getReviewId() || undefined,
    tags,
    tagsStatus,
    studiesSelectedCount,
    applyTags,
    addTags,
  };

  return (
    <div ref={toolbarRef}>
      <Inline
        alignX="spaceBetween"
        spacing={4}
        className={styles.StudyListToolbar}
      >
        <Inline alignX="left" spacing={2}>
          <SelectAllStudies
            isSelected={allStudiesSelected}
            handleStudySelection={handleStudySelection}
            renderDSCheckbox={true}
            dataTrackKey="select-all"
          />
          {isTagsSidebarEnabled ? (
            studiesSelectedCount > 0 && (
              <Button
                {...sidebarTriggerProps('tags', TAGS_SIDEBAR_ID)}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  if (isTagsOpen) {
                    closeSidebar('tags');
                    return;
                  }
                  // Need to set this manually here rather than on render, because
                  // "Add tag" button in the study list also shares the trigger
                  tagsTriggerRef.current = event.currentTarget;
                  openSidebar('tags');
                }}
                type="brand"
                data-pendo-key={
                  isTagsOpen ? 'close-tags-sidebar' : 'open-tags-sidebar'
                }
                className={styles.toggleSidebarButton}
              >
                <Icon icon={IconList.light.faTag} />
                Tag {studyCountText}
              </Button>
            )
          ) : (
            <TagPopover {...tagsPanelProps} />
          )}
          {toolbarOptions.isMergeStudiesEnabled && (
            <MergeStudiesButton
              disabled={isMergeStudiesButtonDisabled}
              studies={studies}
              selectedStudyIds={selectedStudyIds}
              reviewId={reviewId || getReviewId() || ''}
              onMergeSuccess={onMergeSuccess}
            />
          )}
          {toolbarOptions.isDiscussionsEnabled && (
            <StartDiscussionsButton selectedStudyIds={selectedStudyIds} />
          )}
        </Inline>

        <Inline alignX="left" spacing={2}>
          <Button
            {...sidebarTriggerProps('sortAndFilter', sortAndFilterDisclosureId)}
            ref={sortAndFilterToggleRef}
            onClick={() =>
              isSortAndFilterOpen
                ? closeSidebar('sortAndFilter')
                : openSidebar('sortAndFilter')
            }
            type={isSortAndFilterOpen ? 'primary' : 'neutral'}
            variant="outline"
            data-pendo-key={
              isSortAndFilterOpen ? 'hide-sort-and-filter' : 'sort-and-filter'
            }
            className={styles.toggleSidebarButton}
            aria-label={sortAndFilterLabel}
          >
            <Icon icon={IconList.light.faArrowDownArrowUp} size="md" />
            Sort & Filter
            {appliedFilterCount > 0 && (
              <Badge type="primary" variant="solid">
                {appliedFilterCount}
              </Badge>
            )}
          </Button>
          {toolbarOptions.isCriteriaEnabled && (
            <Button
              {...sidebarTriggerProps('criteria', criteriaDisclosureId)}
              ref={criteriaToggleRef}
              onClick={toggleCriteriaPanel}
              type={criteriaTrigger.type}
              variant={criteriaTrigger.variant}
              data-pendo-key={
                isCriteriaOpen ? 'hide-criteria' : criteriaTrigger.pendoKey
              }
              className={styles.toggleSidebarButton}
            >
              <Icon icon={criteriaTrigger.icon} size="md" />
              {criteriaTrigger.label}
            </Button>
          )}
          {toolbarOptions.isHighlightsSidebarEnabled && (
            <Button
              {...sidebarTriggerProps('highlights', highlightsDisclosureId)}
              ref={highlightsToggleRef}
              onClick={() =>
                isHighlightsOpen
                  ? closeSidebar('highlights')
                  : openSidebar('highlights')
              }
              type={isHighlightsOpen || !hasHighlights ? 'primary' : 'neutral'}
              variant={isHighlightsOpen || hasHighlights ? 'outline' : 'solid'}
              data-pendo-key={
                hasHighlights ? 'edit-highlights' : 'add-highlights'
              }
              className={styles.toggleSidebarButton}
            >
              {hasHighlights ? (
                <>
                  <Icon icon={IconList.light.faHighlighter} size="md" />
                  Highlights
                </>
              ) : (
                <>
                  <Icon icon={IconList.light.faPlus} size="md" />
                  Add highlights
                </>
              )}
            </Button>
          )}
        </Inline>
        <div role="region" aria-live="polite" className={styles.ResultsRegion}>
          <UndoVote
            handleUndo={handleUndo}
            handleSkip={handleSkip}
            previouslyVotedStudyTitle={previouslyVotedStudyTitle}
          />

          {appliedFilterCount > 0 && (
            <>
              <div className={styles.ToolbarSeparator} />
              <Inline alignX="left" spacing={3}>
                <Text component="span">
                  {`${numStudiesFound} ${pluralize(
                    'study',
                    numStudiesFound
                  )} found`}
                </Text>
                <Button
                  onClick={resetFiltersAndRefresh}
                  isLoading={isResetting}
                  disabled={filterSortDisplayForm.formState.isSubmitting}
                  variant="outline"
                >
                  <Icon icon={IconList.light.faArrowRotateLeft} />
                  Reset filters
                </Button>
              </Inline>
            </>
          )}
        </div>
      </Inline>
      <SidebarPanel
        currentSidebar={currentSidebar}
        criteria={{
          editableCriteriaRef,
          disclosureId: criteriaDisclosureId,
          toggleRef: criteriaToggleRef,
          showEmptyState: !hasCriteria,
          openWindow: openCriteriaWindow,
          close: closeCriteria,
          onCloseConfirmed: confirmCriteriaClose,
        }}
        highlights={{
          disclosureId: highlightsDisclosureId,
          toggleRef: highlightsToggleRef,
          displayOptions: {
            showAbstracts: isShowingAbstracts,
            toggleAbstracts: handleToggleAbstracts,
            toggleHighlights,
            enableHighlights,
          },
        }}
        sortAndFilter={{
          disclosureId: sortAndFilterDisclosureId,
          toggleRef: sortAndFilterToggleRef,
          form: filterSortDisplayForm,
          sortOptions,
          tags: tags.map((tag) => tag.name),
          isAwaitingOtherReviewer,
          reviewStage: toolbarOptions.reviewStage,
          isResetting,
          onApply: applySortAndFilterThenClose,
          onReset: resetFiltersAndRefresh,
        }}
        tags={{
          disclosureId: TAGS_SIDEBAR_ID,
          toggleRef: tagsTriggerRef,
          panelProps: tagsPanelProps,
        }}
        closeSidebar={closeSidebar}
        focusFallbackRef={toolbarRef}
      />
    </div>
  );
};
